// Educational/Legal document style articles, similar to UBITS and Estrada production data
export default [
    {
        id: '1',
        type: 'article',
        title: 'Chapter 1: Introduction to Constitutional Law',
        pretitle: 'UNIT 1',
        author: 'Prof. Martha Williams, J.D.',
        by_line: 'Constitutional Law Department',
        body: `<div>
            <h2>1.1 Historical Background</h2>
            <p>Constitutional law forms the foundation of modern democratic governance. This chapter explores the historical development of constitutional principles from ancient civilizations through the Enlightenment to contemporary applications.</p>
            
            <h3>1.1.1 Ancient Origins</h3>
            <p>The concept of a written constitution can be traced back to ancient civilizations. The Code of Hammurabi (c. 1750 BCE) represents one of the earliest attempts to codify laws and establish governmental authority.</p>
            
            <h3>1.1.2 Medieval Developments</h3>
            <p>The Magna Carta of 1215 marked a crucial turning point in limiting monarchical power and establishing the principle that even rulers are subject to law. This document influenced constitutional development across Europe and later in the Americas.</p>
            
            <h3>1.1.3 The Enlightenment Era</h3>
            <p>Enlightenment philosophers such as John Locke, Montesquieu, and Rousseau developed theories of natural rights, separation of powers, and social contracts that would become fundamental to modern constitutionalism.</p>
            
            <h2>1.2 Core Constitutional Principles</h2>
            <p>Modern constitutional systems are built upon several key principles:</p>
            <ol>
                <li><strong>Rule of Law:</strong> All individuals and institutions are subject to and accountable under the law.</li>
                <li><strong>Separation of Powers:</strong> Governmental power is divided among legislative, executive, and judicial branches.</li>
                <li><strong>Checks and Balances:</strong> Each branch has mechanisms to limit the powers of the others.</li>
                <li><strong>Federalism:</strong> Power is shared between national and subnational governments.</li>
                <li><strong>Individual Rights:</strong> Constitutional protections for fundamental human rights and civil liberties.</li>
            </ol>
            
            <h2>1.3 Constitutional Interpretation</h2>
            <p>Courts employ various methods to interpret constitutional provisions:</p>
            <ul>
                <li><strong>Originalism:</strong> Interpreting the constitution based on its original meaning at the time of adoption.</li>
                <li><strong>Living Constitution:</strong> Viewing the constitution as a dynamic document that evolves with society.</li>
                <li><strong>Textualism:</strong> Focusing on the plain meaning of constitutional text.</li>
                <li><strong>Purposivism:</strong> Interpreting provisions based on their underlying purposes.</li>
            </ul>
        </div>`,
        start_page: 1,
        end_page: 15,
        location_in_start_page: { x: '10.0', y: '5.0' },
        location_in_end_page: { x: '90.0', y: '95.0' },
        content_metrics: {
            total_number_of_words: 2450,
            reading_time_minutes: 10,
        },
    },
    {
        id: '2',
        type: 'article',
        title: 'Chapter 2: Fundamental Rights and Freedoms',
        pretitle: 'UNIT 1',
        author: 'Prof. Martha Williams, J.D.',
        by_line: 'Constitutional Law Department',
        body: `<div>
            <h2>2.1 The Bill of Rights</h2>
            <p>The first ten amendments to the U.S. Constitution, collectively known as the Bill of Rights, establish fundamental protections for individual liberty against government overreach.</p>
            
            <h3>2.1.1 First Amendment Freedoms</h3>
            <p>The First Amendment protects five fundamental freedoms:</p>
            <table border="1">
                <tr>
                    <th>Freedom</th>
                    <th>Description</th>
                    <th>Key Cases</th>
                </tr>
                <tr>
                    <td>Religion</td>
                    <td>Free exercise and establishment clauses</td>
                    <td>Lemon v. Kurtzman (1971)</td>
                </tr>
                <tr>
                    <td>Speech</td>
                    <td>Protection of expression</td>
                    <td>Brandenburg v. Ohio (1969)</td>
                </tr>
                <tr>
                    <td>Press</td>
                    <td>Media freedom</td>
                    <td>New York Times v. Sullivan (1964)</td>
                </tr>
                <tr>
                    <td>Assembly</td>
                    <td>Right to gather peacefully</td>
                    <td>NAACP v. Alabama (1958)</td>
                </tr>
                <tr>
                    <td>Petition</td>
                    <td>Right to petition government</td>
                    <td>Borough of Duryea v. Guarnieri (2011)</td>
                </tr>
            </table>
            
            <h3>2.1.2 Due Process Rights</h3>
            <p>The Fifth and Fourteenth Amendments guarantee due process of law, protecting individuals from arbitrary government action.</p>
            
            <h2>2.2 Equal Protection</h2>
            <p>The Fourteenth Amendment's Equal Protection Clause prohibits discrimination and requires equal treatment under the law.</p>
        </div>`,
        start_page: 16,
        end_page: 35,
        location_in_start_page: { x: '10.0', y: '5.0' },
        location_in_end_page: { x: '90.0', y: '95.0' },
        content_metrics: {
            total_number_of_words: 3200,
            reading_time_minutes: 13,
        },
    },
    {
        id: '3',
        type: 'article',
        title: 'Case Study: Marbury v. Madison (1803)',
        pretitle: 'LANDMARK CASE',
        author: 'Editorial Team',
        body: `<div>
            <h2>Facts of the Case</h2>
            <p>In the final days of John Adams's presidency, he appointed William Marbury as a justice of the peace. However, the commission was not delivered before Thomas Jefferson took office. Jefferson's Secretary of State, James Madison, refused to deliver the commission.</p>
            
            <h2>Legal Question</h2>
            <p>Does the Supreme Court have the authority to issue a writ of mandamus compelling Madison to deliver Marbury's commission?</p>
            
            <h2>Court's Decision</h2>
            <p>Chief Justice John Marshall's opinion established the principle of judicial review, holding that:</p>
            <ol>
                <li>Marbury had a right to his commission</li>
                <li>The law provided a remedy for violation of this right</li>
                <li>However, the Supreme Court lacked jurisdiction because the relevant provision of the Judiciary Act of 1789 was unconstitutional</li>
            </ol>
            
            <h2>Significance</h2>
            <p>This case established the Supreme Court's power to review and invalidate laws that conflict with the Constitution, fundamentally shaping the American system of checks and balances.</p>
            
            <blockquote>
                "It is emphatically the province and duty of the judicial department to say what the law is."
                <cite>- Chief Justice John Marshall</cite>
            </blockquote>
        </div>`,
        start_page: 36,
        end_page: 40,
        location_in_start_page: { x: '10.0', y: '10.0' },
        location_in_end_page: { x: '90.0', y: '85.0' },
        content_metrics: {
            total_number_of_words: 450,
            reading_time_minutes: 2,
        },
    },
    {
        id: '4',
        type: 'article',
        title: 'Practice Exercises',
        pretitle: 'SELF-ASSESSMENT',
        body: `<div>
            <h2>Multiple Choice Questions</h2>
            <ol>
                <li>
                    <p>Which principle allows each branch of government to limit the powers of the other branches?</p>
                    <p>a) Federalism<br>
                    b) Checks and balances<br>
                    c) Judicial review<br>
                    d) Executive privilege</p>
                </li>
                <li>
                    <p>The concept of "living constitution" refers to:</p>
                    <p>a) The original intent of the framers<br>
                    b) A dynamic interpretation that evolves with society<br>
                    c) Strict textual interpretation<br>
                    d) Constitutional amendments only</p>
                </li>
            </ol>
            
            <h2>Essay Questions</h2>
            <ol>
                <li>Discuss the tension between originalism and living constitutionalism in modern judicial interpretation.</li>
                <li>Analyze the impact of Marbury v. Madison on the American system of government.</li>
                <li>Evaluate the effectiveness of the separation of powers in preventing governmental abuse.</li>
            </ol>
            
            <h2>Case Analysis</h2>
            <p>Read the following scenario and identify the constitutional issues:</p>
            <p class="scenario">A state legislature passes a law requiring all public school students to recite a prayer at the beginning of each school day. A group of parents challenges this law in court.</p>
            <p>Consider: Which constitutional provisions are relevant? How might different interpretive approaches lead to different outcomes?</p>
        </div>`,
        start_page: 41,
        end_page: 45,
        location_in_start_page: { x: '10.0', y: '8.0' },
        location_in_end_page: { x: '90.0', y: '92.0' },
        content_metrics: {
            total_number_of_words: 580,
            reading_time_minutes: 3,
        },
    },
    {
        id: '5',
        type: 'article',
        title: 'Glossary of Terms',
        pretitle: 'REFERENCE',
        body: `<div>
            <dl>
                <dt><strong>Amicus Curiae</strong></dt>
                <dd>A "friend of the court" brief filed by a non-party to provide information or arguments to the court.</dd>
                
                <dt><strong>Certiorari</strong></dt>
                <dd>A writ by which a higher court reviews a decision of a lower court.</dd>
                
                <dt><strong>De Facto</strong></dt>
                <dd>Existing in fact, though not necessarily ordained by law.</dd>
                
                <dt><strong>De Jure</strong></dt>
                <dd>Existing by right or according to law.</dd>
                
                <dt><strong>Habeas Corpus</strong></dt>
                <dd>A writ requiring a person under arrest to be brought before a judge.</dd>
                
                <dt><strong>Judicial Review</strong></dt>
                <dd>The power of courts to review and potentially invalidate legislative and executive actions.</dd>
                
                <dt><strong>Precedent</strong></dt>
                <dd>A legal principle established in a previous case that guides future similar cases.</dd>
                
                <dt><strong>Stare Decisis</strong></dt>
                <dd>The doctrine that courts should follow precedents established in previous decisions.</dd>
            </dl>
        </div>`,
        start_page: 46,
        end_page: 48,
        location_in_start_page: { x: '10.0', y: '10.0' },
        location_in_end_page: { x: '90.0', y: '50.0' },
        content_metrics: {
            total_number_of_words: 320,
            reading_time_minutes: 2,
        },
    },
    {
        id: '6',
        type: 'image',
        title: 'Figure 1.1: Structure of the Federal Court System',
        image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
        image_caption: 'Hierarchical organization of federal courts in the United States',
        image_name: 'court_structure.jpg',
        start_page: 8,
        end_page: 8,
        location_in_start_page: { x: '25.0', y: '40.0' },
    },
    {
        id: '7',
        type: 'article',
        title: 'Bibliography',
        pretitle: 'REFERENCES',
        body: `<div>
            <h2>Primary Sources</h2>
            <ul>
                <li>U.S. Constitution and Amendments</li>
                <li>The Federalist Papers (Hamilton, Madison, Jay)</li>
                <li>Marbury v. Madison, 5 U.S. 137 (1803)</li>
                <li>McCulloch v. Maryland, 17 U.S. 316 (1819)</li>
            </ul>
            
            <h2>Secondary Sources</h2>
            <ul>
                <li>Chemerinsky, E. (2019). <em>Constitutional Law: Principles and Policies</em> (6th ed.). Wolters Kluwer.</li>
                <li>Tribe, L. H. (2000). <em>American Constitutional Law</em> (3rd ed.). Foundation Press.</li>
                <li>Amar, A. R. (2012). <em>America's Unwritten Constitution</em>. Basic Books.</li>
                <li>Breyer, S. (2010). <em>Making Our Democracy Work</em>. Knopf.</li>
            </ul>
        </div>`,
        start_page: 47,
        end_page: 48,
        location_in_start_page: { x: '10.0', y: '10.0' },
        location_in_end_page: { x: '90.0', y: '60.0' },
        content_metrics: {
            total_number_of_words: 180,
            reading_time_minutes: 1,
        },
    },
];
